'use client'

import { useEffect, useRef, useState } from 'react'

type MotionIllustProps = {
  /** public/images/illust 配下のモーション SVG。SVG 以外（webp 等）は通常の <img> で出す */
  src: string
  alt?: string
  width: number
  height: number
  className?: string
  /** eager はファーストビュー用。lazy は画面に近づいてから読み込む */
  loading?: 'lazy' | 'eager'
}

/** 画面に入る少し手前から読み込み始める */
const preloadMargin = '200px'

const setPlaying = (object: HTMLObjectElement | null, playing: boolean) => {
  for (const animation of object?.contentDocument?.getAnimations?.() ?? []) {
    if (playing) animation.play()
    else animation.pause()
  }
}

/**
 * SVG 内の CSS アニメーション（素材はすべて infinite ループ）を、画面に入っている間だけ再生するイラスト。
 * 画面から外れたらその場で止め、画面に戻ると止めた位置から続きを再生する。
 *
 * <img> で読むと SVG 内のアニメーションを外から止められないため <object> で読み、
 * 同一オリジンの contentDocument から getAnimations() で pause / play する
 * （グローバルメニューの MenuIllust と同じ方式。あちらはホバー連動なので別実装）。
 *
 * <img> の置き換えとして使う。<object> は width / height 属性から縦横比を取らないので、
 * height 属性は付けずに aspect-ratio で比率を保つ
 */
export const MotionIllust = ({
  src,
  alt = '',
  width,
  height,
  className,
  loading = 'lazy'
}: MotionIllustProps) => {
  const objectRef = useRef<HTMLObjectElement>(null)
  const visibleRef = useRef(false)
  // display: none の側（SP / PC で出し分ける片方）は交差しないので読み込まれない
  const [shouldLoad, setShouldLoad] = useState(loading === 'eager')
  const isSvg = src.endsWith('.svg')

  useEffect(() => {
    const object = objectRef.current
    if (!object) return
    const loader = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoad(true)
          loader.disconnect()
        }
      },
      { rootMargin: preloadMargin }
    )
    const player = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1]
      visibleRef.current = entry?.isIntersecting ?? false
      setPlaying(object, visibleRef.current)
    })
    loader.observe(object)
    player.observe(object)
    return () => {
      loader.disconnect()
      player.disconnect()
    }
  }, [])

  if (!isSvg) {
    return (
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        className={className}
        loading={loading}
      />
    )
  }

  return (
    <object
      ref={objectRef}
      data={shouldLoad ? src : undefined}
      type="image/svg+xml"
      width={width}
      style={{ aspectRatio: `${width} / ${height}` }}
      className={`pointer-events-none ${className ?? ''}`}
      tabIndex={-1}
      role={alt ? 'img' : undefined}
      aria-label={alt || undefined}
      aria-hidden={alt ? undefined : true}
      // 読み込み直後はアニメーションが自動で走るので、その時点の表示状態に合わせる
      onLoad={() => setPlaying(objectRef.current, visibleRef.current)}
    />
  )
}
